<template>
  <v-expansion-panels popout v-model="openPanels" tile accordion>
    <v-expansion-panel
      v-for="(journalEntryByBusinessEntity, i) in journalEntriesByAccount"
      :key="journalEntryByBusinessEntity.businessEntityName"
    >
      <v-expansion-panel-header @click="assignTableHeightWithDelay">
        {{ journalEntryByBusinessEntity.businessEntityName }}
        <v-spacer/>
        <template #actions>
          {{ formatMoney(journalEntryByBusinessEntity.amount) }}
          <v-icon
            class="ml-2"
          >
            mdi-chevron-down
          </v-icon
          >
        </template>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-data-table
          :id="'journal-entry-expansion-panel-table-'.concat(i)"
          v-if="journalEntryByBusinessEntity.journalEntries.length > 0"
          :headers="headers"
          :items.sync="journalEntryByBusinessEntity.journalEntries"
          :hide-default-footer="journalEntryByBusinessEntity.journalEntries.length < 10"
          dense
          :height="tableHeight"
          @update:options="tableOptionsUpdated"
        >
          <template #item.activityName="{ item }">
            <Icon icon="weight-alert" :tooltipText="$t('penalty')" :small="false" v-if="item.specialization === TemplateSpecialization.Penalty.value"/>
            {{item.activityName}}
          </template>
          <template v-slot:item.amount="{ item }">{{
            `${formatMoney(item.amount)}`
          }}</template>
          <template v-slot:item.rate="{ item }">
            <span>{{ formatMoney(item.computedAtRate) }}</span>
          </template>
          <template #item.payOn="{item}">{{
            payOn(item.computedAtPayOn)
          }}</template>
          <template #item.payBy="{item}">{{
            payBy(item.computedAtPayBy)
          }}</template>
          <template v-slot:item.createdAt="{ item }">{{
            utcToLocalDate(item.ticketCreatedAt)
          }}</template>
          <template v-slot:item.actions="{ item }">
            <Icon
              iconColor="black"
              icon="mdi-information-outline"
              :tooltipText="$t('details')"
              :disabled="item.recoveryMethod === 1"
              @icon-clicked="viewJournalEntry(item)"
            >
            </Icon>
          </template>
        </v-data-table>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { formatMoney } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { ticketJournalEntryHeaders } from '@/headers/JournalEntry.js'
import { PayOn, PayBy, TemplateSpecialization } from '@/utils/Enumerations.js'
import { tableHeight } from '@/utils/componentHelpers.js'
import { mapGetters } from 'vuex'

export default {
  name: 'JournalEntryExpansionPanels',

  props: {
    journalEntries: {
      type: Array,
      required: true
    },
    ticket: {
      type: Object,
      required: false
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    TemplateSpecialization,
    openPanels: [],
    currentTableOptions: {},
    tableHeight: 'auto'
  }),

  created () {
    this.openPanels = this.journalEntriesByAccount.length === 1 ? 0 : null
    this.assignTableHeightWithDelay()
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),

    headers () {
      const headers = ticketJournalEntryHeaders(this.$i18n.locale)
      headers.pop()
      return headers
    },

    journalEntriesByAccount () {
      const journalEntriesByAccount = []
      this.journalEntries.forEach(p => {
        const entityIndex = journalEntriesByAccount.findIndex(pbv => pbv.businessEntityName === p.businessEntityName)
        if (entityIndex === -1) {
          journalEntriesByAccount.push({
            businessEntityName: p.businessEntityName,
            amount: p.amount,
            journalEntries: [p]
          })
        } else {
          journalEntriesByAccount[entityIndex].journalEntries.push(p)
          journalEntriesByAccount[entityIndex].amount += p.amount
        }
      })

      return journalEntriesByAccount
    }
  },

  watch: {
    topDialogState: {
      handler () {
        this.assignTableHeightWithDelay(150)
      },
      deep: true
    },

    '$vuetify.breakpoint.width' () { this.assignTableHeight() },
    '$vuetify.breakpoint.height' () { this.assignTableHeight() }
  },

  methods: {
    formatMoney,
    utcToLocalDate,
    payOn: (x) => PayOn.fromInt(x),
    payBy: (x) => PayBy.fromInt(x),

    tableOptionsUpdated (tableOptions) {
      this.currentTableOptions = tableOptions
    },

    assignTableHeightWithDelay (delay = 50) {
      setTimeout(() => {
        this.assignTableHeight()
      }, delay)
    },

    assignTableHeight () {
      this.tableHeight = tableHeight({
        tableElementId: 'journal-entry-expansion-panel-table-'.concat(this.openPanel),
        offset: 600,
        dataLength: this.journalEntriesByAccount?.length || 0
      })
    }
  }
}
</script>
